import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import Spinwheel from "../../components/common/Spinwheel";
import { getCompanySettingsApi, removeCompanyLetterheadApi, updateCompanyStorageSettingsApi, uploadCompanyLetterheadApi, uploadCompanyLogoApi, removeCompanyLogoApi } from "../../utils/ApiEndpointFunctions";
import { useUserContext } from '../../context/userContext';
import { getDbS3Url } from "../../utils/HelperFuncitons";
import "./CompanySettings.css";

const emptyStorage = { bucketAccessKeyId: "", bucketSecretAccessKey: "", bucketRegion: "", bucketName: "", s3Url: "" };
const clearAwsModalId = "clearAwsSettingsConfirmation";
const removeLetterheadModalId = "removeLetterheadConfirmation";
const removeLogoModalId = 'removeCompanyLogoConfirmation';

export default function CompanySettings() {
  const { langPreference, setUserDetails } = useUserContext();
  const [settings, setSettings] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [storage, setStorage] = useState(emptyStorage);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [uploading, setUploading] = useState("");
  const [removing, setRemoving] = useState("");
  const [letterheadToRemove, setLetterheadToRemove] = useState("");

  const updateLogo = (data) => {
    setSettings(data);
    setUserDetails(current => String(current?.siteId?.companyId?._id) === String(data.companyId) ? {
      ...current, siteId: { ...current.siteId, companyId: { ...current.siteId.companyId, companyLogo: data.companyLogo, s3Url: data.s3Url } },
    } : current);
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 2 * 1024 * 1024) {
      toast.error(langPreference?.company_logo_file_help || 'Use PNG, JPEG or WebP, up to 2 MB.');
      return;
    }
    const payload = new FormData();
    payload.append('file', file);
    setUploading('logo');
    try {
      const response = await uploadCompanyLogoApi(payload);
      updateLogo(response.data.data);
      toast.success(langPreference?.company_logo_uploaded || 'Company logo uploaded.');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Unable to upload company logo.');
    } finally { setUploading(''); }
  };

  const removeLogo = async () => {
    setRemoving('logo');
    try {
      const response = await removeCompanyLogoApi();
      updateLogo(response.data.data);
      toast.success(langPreference?.company_logo_removed || 'Company logo removed.');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Unable to remove company logo.');
    } finally { setRemoving(''); }
  };

  const applySettings = useCallback((data) => {
    setSettings(data);
    setStorage({
      bucketAccessKeyId: data.bucketAccessKeyId || "",
      bucketSecretAccessKey: "",
      bucketRegion: data.bucketRegion || "",
      bucketName: data.bucketName || "",
      s3Url: data.s3Url || "",
    });
  }, []);

  const loadSettings = useCallback(async () => {
    setLoadError("");
    try {
      const response = await getCompanySettingsApi();
      if (response?.data?.status !== 1 || !response?.data?.data) {
        throw new Error("Company Settings API is not available on this server.");
      }
      applySettings(response.data.data);
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Unable to load company settings.";
      setLoadError(message);
      toast.error(message);
    }
  }, [applySettings]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const saveStorage = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await updateCompanyStorageSettingsApi(storage);
      applySettings(response.data.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to save storage settings.");
    } finally { setSaving(false); }
  };

  const clearStorage = async () => {
    setClearing(true);
    try {
      const response = await updateCompanyStorageSettingsApi(emptyStorage);
      applySettings(response.data.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to clear AWS settings.");
    } finally { setClearing(false); }
  };

  const uploadLetterhead = async (position, file) => {
    if (!file) return;
    const payload = new FormData();
    payload.append("position", position);
    payload.append("file", file);
    setUploading(position);
    try {
      const response = await uploadCompanyLetterheadApi(payload);
      applySettings(response.data.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to upload letterhead image.");
    } finally { setUploading(""); }
  };

  const removeLetterhead = async () => {
    if (!letterheadToRemove) return;
    setRemoving(letterheadToRemove);
    try {
      const response = await removeCompanyLetterheadApi(letterheadToRemove);
      applySettings(response.data.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to remove the letterhead image.");
    } finally {
      setRemoving("");
      setLetterheadToRemove("");
    }
  };

  if (loadError) return <div className="main-content"><div className="container-fluid py-4">
    <div className="alert alert-danger">
      <h5>Unable to load company settings</h5>
      <p className="mb-3">{loadError}</p>
      <button className="btn btn-outline-danger" onClick={loadSettings}>Retry</button>
    </div>
  </div></div>;
  if (!settings) return <div className="main-content"><div className="d-flex justify-content-center py-5"><Spinwheel /></div></div>;
  const assetUrl = (key) => key ? (/^https?:\/\//i.test(key) ? key : `${getDbS3Url(settings.s3Url).replace(/\/$/, '')}/${key.split('/').map(encodeURIComponent).join('/')}`) : "";
  const busy = saving || clearing || Boolean(uploading) || Boolean(removing);

  return <div className="main-content"><div className="container-fluid">
    <h3 className="font-blue mb-1">Company Settings</h3>

    <div className="card p-4 mb-4">
      <h4>{langPreference?.company_logo || 'Company Logo'}</h4>
      <p className="text-muted">{langPreference?.company_logo_help || 'Upload a logo for this company. It appears across its sites in web and mobile headers and on supported documents.'}</p>
      <div className="d-flex align-items-center flex-wrap gap-4">
        <div className="company-logo-preview">{settings.companyLogo
          ? <img src={assetUrl(settings.companyLogo)} alt={langPreference?.company_logo || 'Company Logo'} />
          : <span className="text-muted">{langPreference?.not_uploaded || 'Not uploaded'}</span>}</div>
        <div>
          <div className="d-flex gap-2 flex-wrap">
            <label className={`btn btn-outline-primary mb-0 ${busy ? 'disabled' : ''}`}>
              {uploading === 'logo' ? (langPreference?.loading || 'Loading...') : settings.companyLogo ? (langPreference?.replace_logo || 'Replace Logo') : (langPreference?.upload_logo || 'Upload Logo')}
              <input type="file" aria-label={langPreference?.upload_logo || 'Upload Logo'} className="d-none" accept="image/png,image/jpeg,image/webp" disabled={busy} onChange={event => { const file = event.target.files?.[0]; event.target.value = ''; uploadLogo(file); }} />
            </label>
            {settings.companyLogo && <button type="button" className="btn btn-outline-danger" disabled={busy} data-bs-toggle="modal" data-bs-target={`#${removeLogoModalId}`}>{langPreference?.remove_logo || 'Remove Logo'}</button>}
          </div>
          <small className="d-block text-muted mt-2">{langPreference?.company_logo_file_help || 'Use PNG, JPEG or WebP, up to 2 MB.'}</small>
        </div>
      </div>
    </div>

    <div className="card p-4 mb-4"><h4>AWS storage</h4><p className="text-muted">When all fields are configured and verified, every client upload is stored in this bucket. Clear all fields to use MMS storage.</p>
      <form onSubmit={saveStorage}><div className="row g-3">
        <div className="col-md-6"><label className="form-label">Access key ID</label><input className="form-control" value={storage.bucketAccessKeyId} onChange={(event) => setStorage({ ...storage, bucketAccessKeyId: event.target.value })} /></div>
        <div className="col-md-6"><label className="form-label">Secret access key</label><input type="password" className="form-control" value={storage.bucketSecretAccessKey} onChange={(event) => setStorage({ ...storage, bucketSecretAccessKey: event.target.value })} placeholder={settings.hasBucketSecretAccessKey ? "Leave blank to keep current secret" : ""} /></div>
        <div className="col-md-4"><label className="form-label">Region</label><input className="form-control" value={storage.bucketRegion} onChange={(event) => setStorage({ ...storage, bucketRegion: event.target.value })} placeholder="ap-south-1" /></div>
        <div className="col-md-4"><label className="form-label">Bucket name</label><input className="form-control" value={storage.bucketName} onChange={(event) => setStorage({ ...storage, bucketName: event.target.value })} /></div>
        <div className="col-md-4"><label className="form-label">Public asset URL</label><input className="form-control" value={storage.s3Url} onChange={(event) => setStorage({ ...storage, s3Url: event.target.value })} placeholder="Generated automatically if empty" /></div>
        <div className="col-12 d-flex gap-2">
          <button className="btn btn-primary" disabled={busy}>{saving ? "Validating and saving..." : "Save AWS settings"}</button>
          <button type="button" className="btn btn-outline-danger" disabled={busy} data-bs-toggle="modal" data-bs-target={`#${clearAwsModalId}`}>{clearing ? "Clearing..." : "Clear AWS settings"}</button>
        </div>
      </div></form>
    </div>

    <div className="card p-4"><h4>Letterhead</h4><p className="text-muted">Upload your company letterhead header and footer. They will appear at the top and bottom of every page in all generated PDFs.<br />“Powered by MMS” will always appear immediately above the footer.</p>
      {["header", "footer"].map((position) => { const key = position === "header" ? settings.letterheadHeader : settings.letterheadFooter; return <div className="row align-items-center border rounded p-3 mb-3" key={position}>
        <div className="col-md-3 text-capitalize fw-semibold">{position} image</div><div className="col-md-6">{key ? <img src={assetUrl(key)} alt={`Letterhead ${position}`} style={{ maxWidth: "100%", maxHeight: "100px" }} /> : <span className="text-muted">Not uploaded</span>}</div>
        <div className="col-md-3 d-flex flex-wrap gap-2">
          <label className="btn btn-outline-primary mb-0">{uploading === position ? "Uploading..." : `Upload ${position}`}<input className="d-none" type="file" accept="image/png,image/jpeg,image/webp" disabled={busy} onChange={(event) => uploadLetterhead(position, event.target.files?.[0])} /></label>
          {key && <button type="button" className="btn btn-outline-danger" disabled={busy} data-bs-toggle="modal" data-bs-target={`#${removeLetterheadModalId}`} onClick={() => setLetterheadToRemove(position)}>{removing === position ? "Removing..." : "Remove"}</button>}
        </div>
      </div>; })}
    </div>
    <div className="modal fade aws-clear-modal" id={removeLogoModalId} tabIndex="-1" aria-labelledby={`${removeLogoModalId}Title`} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered"><div className="modal-content">
        <button type="button" className="btn-close aws-clear-modal__close" data-bs-dismiss="modal" aria-label="Close"></button>
        <div className="aws-clear-modal__body">
          <h3 id={`${removeLogoModalId}Title`}>{langPreference?.remove_company_logo || 'Remove company logo?'}</h3>
          <p>{langPreference?.remove_company_logo_help || 'The company logo will be removed for all sites belonging to this company.'}</p>
        </div>
        <div className="aws-clear-modal__actions">
          <button type="button" className="btn aws-clear-modal__cancel" data-bs-dismiss="modal">{langPreference?.cancel || 'Cancel'}</button>
          <button type="button" className="btn aws-clear-modal__confirm" data-bs-dismiss="modal" disabled={busy} onClick={removeLogo}>{langPreference?.remove_logo || 'Remove Logo'}</button>
        </div>
      </div></div>
    </div>
    <div className="modal fade aws-clear-modal" id={clearAwsModalId} tabIndex="-1" aria-labelledby={`${clearAwsModalId}Title`} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <button type="button" className="btn-close aws-clear-modal__close" data-bs-dismiss="modal" aria-label="Close"></button>
          <div className="aws-clear-modal__body">
            <div className="aws-clear-modal__icon" aria-hidden="true"><i className="bi bi-cloud-slash"></i></div>
            <h3 id={`${clearAwsModalId}Title`}>Clear AWS settings?</h3>
            <p>Your saved AWS credentials and bucket configuration will be removed. Future uploads will use MMS storage.</p>
          </div>
          <div className="aws-clear-modal__actions">
            <button type="button" className="btn aws-clear-modal__cancel" data-bs-dismiss="modal">Cancel</button>
            <button type="button" className="btn aws-clear-modal__confirm" data-bs-dismiss="modal" onClick={clearStorage}>Clear settings</button>
          </div>
        </div>
      </div>
    </div>
    <div className="modal fade aws-clear-modal" id={removeLetterheadModalId} tabIndex="-1" aria-labelledby={`${removeLetterheadModalId}Title`} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <button type="button" className="btn-close aws-clear-modal__close" data-bs-dismiss="modal" aria-label="Close"></button>
          <div className="aws-clear-modal__body">
            <div className="aws-clear-modal__icon" aria-hidden="true"><i className="bi bi-image"></i></div>
            <h3 id={`${removeLetterheadModalId}Title`}>Remove letterhead {letterheadToRemove}?</h3>
            <p>The {letterheadToRemove} image will no longer appear on generated PDF pages.</p>
          </div>
          <div className="aws-clear-modal__actions">
            <button type="button" className="btn aws-clear-modal__cancel" data-bs-dismiss="modal" onClick={() => setLetterheadToRemove("")}>Cancel</button>
            <button type="button" className="btn aws-clear-modal__confirm" data-bs-dismiss="modal" onClick={removeLetterhead}>Remove {letterheadToRemove}</button>
          </div>
        </div>
      </div>
    </div>
  </div></div>;
}
